import * as React from 'react';
import {Link} from 'react-router-dom';

// @ts-expect-error
import capitalize from 'underscore.string/capitalize';

import type {FormattedValue} from 'nutshell-core/types';

import {colors} from '../colors/colors';

import Arrow from './arrow.svg';
// @ts-expect-error
import {calculatePercentDifference, convertValueToString, formatPeriod} from './metrics-helpers';

import styles from './metric-row.module.css';

const TITLE = {
    sales: 'Sales',
    openleads: 'Open leads',
    newleads: 'New leads',
    companiesIdentified: 'Companies identified',
    convertedToLeads: 'Converted to leads',
} as const;

export type MetricValue = {
    startTime?: number; // UNIX secs,
    endTime?: number; // UNIX secs,
    count: number;
    amount?: FormattedValue | null | undefined;
};

type Props = {
    currentValue: MetricValue;
    previousValue: MetricValue;
    deltaDirection: 'up' | 'down' | 'neutral';
    deltaMood: 'positive' | 'negative' | 'neutral';
    htmlUrl: string;
    name: string;
    periodString: string;
    timespanString: string;
};

export function MetricRow(props: Props) {
    const percentChange = calculatePercentDifference(props.currentValue, props.previousValue);
    const fillColor = {
        positive: colors.green,
        negative: colors.red,
        neutral: colors.greyLt,
    }[props.deltaMood];

    const content = (
        <React.Fragment>
            {props.deltaDirection === 'neutral' ? (
                <div className={styles['arrow-unchanged']} />
            ) : (
                <div className={styles[`arrow-${props.deltaDirection}`]}>
                    <Arrow fill={fillColor} />
                </div>
            )}
            <div className={styles['details-container']}>
                {/* @ts-expect-error */}
                <span className={styles.title}>{TITLE[props.name] || props.name}</span>
                <div className={styles['current-value-container']}>
                    {renderCurrentValue(props.currentValue)}
                </div>
                {renderDeltaDescription(
                    props.deltaDirection,
                    percentChange,
                    formatPeriod(props.periodString, 'previous'),
                    props.previousValue,
                    props.timespanString,
                    props.name
                )}
            </div>
        </React.Fragment>
    );

    return (
        <div className={styles['row-container']}>
            {/* @ts-expect-error */}
            <Link to={props.htmlUrl} className={styles.link}>
                {content}
            </Link>
        </div>
    );
}

// @ts-expect-error
function renderCurrentValue(currentValue) {
    if (currentValue.amount) {
        return (
            <div className={styles['current-value']}>
                <span className={styles['currency-prefix']}>{currentValue.amount.prefix}</span>
                <span className={styles['currency-value']}>{currentValue.amount.formatted}</span>
                <span className={styles['currency-suffix']}>{currentValue.amount.suffix}</span>
            </div>
        );
    }

    return <div className={styles['current-value']}>{currentValue.count}</div>;
}

// @ts-expect-error
function renderDeltaDescription(direction, percentDiff, period, previousVal, timespanString, name) {
    if (direction === 'neutral' && previousVal.count === 0) {
        return undefined;
    }

    let deltaText = `Unchanged from ${timespanString || period}`;

    let verb = 'conversions';
    if (name === 'Companies identified') {
        verb = 'identifications';
    }

    if (percentDiff === Infinity) {
        deltaText = `${convertValueToString(previousVal)} ${verb} ${timespanString || period}`;
    } else if (direction !== 'neutral') {
        const percent = Math.round(Math.abs(percentDiff) * 100);
        deltaText = `${capitalize(direction)} ${percent}% from ${convertValueToString(
            previousVal
        )} ${verb} ${timespanString || period}`;
    }

    return <div className={styles['delta-description-container']}>{deltaText}</div>;
}
